import { useState, useRef, useEffect } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { PLAYING_LEVELS, ACCOMMODATION_GROUPS, roomLabel } from '../constants/padel';

// Public, unauthenticated Padelante lead-capture form.
// Reached via QR codes at https://msttoursportal.co.uk/padelante?src=<channel>.
// Submissions land in the `padelanteLeads` collection → Team Database ▸ Padelante Leads.

const NAVY  = '#1B4079';
const GREEN = '#8DC63F';

// Maps the ?src= QR parameter to the canonical Source value shown on the sales page.
const SOURCE_MAP = {
  instagram: 'Instagram',
  facebook:  'Facebook',
  tiktok:    'TikTok',
  qr:        'QR code',
};

function resolveSource() {
  const raw = new URLSearchParams(window.location.search).get('src') || '';
  return SOURCE_MAP[raw.toLowerCase()] || 'Website';
}

// Full-screen viewer for the playing level table image.
function LevelTableModal({ onClose }) {
  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/60 flex items-start justify-center overflow-y-auto p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-[14px] shadow-xl w-full max-w-[560px] my-auto overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E7EB]">
          <p className="text-[14px] font-bold" style={{ color: NAVY }}>Padel playing level table</p>
          <button type="button" onClick={onClose}
            className="text-[#9CA3AF] hover:text-[#374151] transition text-2xl leading-none">×</button>
        </div>
        <img
          src="/padel-playing-level-table.jpeg"
          alt="Padel playing level table — ratings 1.0 to 7.0 with playing descriptions and divisions"
          className="w-full h-auto block"
        />
        <div className="px-4 py-3 border-t border-[#E5E7EB] text-right">
          <button type="button" onClick={onClose}
            className="px-4 py-2 text-white text-[13px] font-semibold rounded-[8px]"
            style={{ background: NAVY }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PadelanteLead() {
  const [form, setForm] = useState({
    contactName: '', email: '', phone: '', playingLevel: '',
    clubSchool: '', partySize: '', accommodation: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [showLevels, setShowLevels] = useState(false);

  // Anti-spam: page-mount timestamp + honeypot field, enforced in firestore.rules
  const mountedAtRef = useRef(new Date());
  const [honeypot, setHoneypot] = useState('');
  const sourceRef = useRef(resolveSource());

  // Keep this page out of search engines
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => document.head.removeChild(meta);
  }, []);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (honeypot) return; // silently drop — bot filled a field real users never see
    setSubmitting(true);
    try {
      await addDoc(collection(db, 'padelanteLeads'), {
        clubSchool: form.clubSchool.trim(),
        contactName: form.contactName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        playingLevel: form.playingLevel,
        // Rules require a whole number, so round rather than reject a "8.5".
        partySize: Math.max(0, Math.round(Number(form.partySize) || 0)),
        accommodation: form.accommodation,
        status: 'New',
        source: sourceRef.current,
        assignedTo: '',
        assignedToName: '',
        noteLog: [],
        origin: 'padelante-web',
        honeypot: '',
        clientStartedAt: mountedAtRef.current,
        createdAt: serverTimestamp(),
        lastContactedAt: null,
        createdBy: '',
      });
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      setError('Something went wrong — please try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  }

  const inputCls =
    'w-full border border-[#D1D5DB] rounded-[10px] px-4 py-3 text-[15px] bg-white focus:outline-none focus:ring-2 focus:ring-[#1B4079] focus:border-[#1B4079]';

  return (
    <div className="min-h-screen bg-[#F3F4F6] flex flex-col items-center px-4 py-8">
      <div className="w-full max-w-[520px]">
        {/* Header */}
        <div className="text-center mb-6">
          <img
            src="/Padelante%20Logo.png"
            alt="Padelante"
            className="h-20 w-20 mx-auto mb-3"
          />
          <h1 className="text-[24px] font-extrabold" style={{ color: NAVY }}>
            Padelante Tours &amp; Camps
          </h1>
          <p className="text-[14px] text-[#6B7280] mt-1">
            Padel tours &amp; camps in Alicante. Leave your details and our team will be in touch.
          </p>
        </div>

        {submitted ? (
          <div className="bg-white rounded-[16px] border border-[#E5E7EB] shadow-sm p-8 text-center">
            <div
              className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center"
              style={{ background: GREEN }}
            >
              <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-[20px] font-bold" style={{ color: NAVY }}>Thanks — we've got your details!</h2>
            <p className="text-[14px] text-[#6B7280] mt-2">
              A member of the Padelante team will get back to you shortly.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-white rounded-[16px] border border-[#E5E7EB] shadow-sm p-6 sm:p-7 space-y-4">
            {/* Honeypot — hidden from real users */}
            <input
              type="text" tabIndex={-1} autoComplete="off"
              value={honeypot} onChange={(e) => setHoneypot(e.target.value)}
              className="hidden" aria-hidden="true"
            />

            <div>
              <label className="block text-[13px] font-semibold text-[#374151] mb-1.5">
                Name <span className="text-[#DC2626]">*</span>
              </label>
              <input type="text" required value={form.contactName} onChange={set('contactName')}
                placeholder="Full name" className={inputCls} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[13px] font-semibold text-[#374151] mb-1.5">
                  Email <span className="text-[#DC2626]">*</span>
                </label>
                <input type="email" required value={form.email} onChange={set('email')}
                  placeholder="you@email.com" className={inputCls} />
              </div>
              <div>
                <label className="block text-[13px] font-semibold text-[#374151] mb-1.5">
                  Phone <span className="text-[#DC2626]">*</span>
                </label>
                <input type="tel" required value={form.phone} onChange={set('phone')}
                  placeholder="+44 7700 000000" className={inputCls} />
              </div>
            </div>

            <div>
              <label className="block text-[13px] font-semibold text-[#374151] mb-1.5">
                Playing level <span className="text-[#DC2626]">*</span>
              </label>
              <select required value={form.playingLevel} onChange={set('playingLevel')}
                className={inputCls}>
                <option value="">Select your level…</option>
                {PLAYING_LEVELS.map(([level, desc]) => (
                  <option key={level} value={level}>{level} — {desc}</option>
                ))}
              </select>
              <button
                type="button" onClick={() => setShowLevels(true)}
                className="mt-1.5 text-[12px] font-semibold underline hover:no-underline"
                style={{ color: NAVY }}
              >
                Not sure? View the padel playing level table
              </button>
            </div>

            <div>
              <label className="block text-[13px] font-semibold text-[#374151] mb-1.5">
                Club you most often play at <span className="text-[#DC2626]">*</span>
              </label>
              <input type="text" required value={form.clubSchool} onChange={set('clubSchool')}
                placeholder="e.g. Alicante Padel Club" className={inputCls} />
            </div>

            <div>
              <label className="block text-[13px] font-semibold text-[#374151] mb-1.5">
                Expected number of travellers in your party <span className="text-[#DC2626]">*</span>
              </label>
              <input type="number" min="1" max="200" step="1" required value={form.partySize}
                onChange={set('partySize')} placeholder="e.g. 8" className={inputCls} />
            </div>

            <div>
              <label className="block text-[13px] font-semibold text-[#374151] mb-1.5">
                Accommodation preference <span className="text-[#DC2626]">*</span>
              </label>
              <select required value={form.accommodation} onChange={set('accommodation')}
                className={inputCls}>
                <option value="">Select a preference…</option>
                {ACCOMMODATION_GROUPS.map(([hotel, options]) => (
                  <optgroup key={hotel} label={hotel}>
                    {options.map((o) => <option key={o} value={o}>{roomLabel(o)}</option>)}
                  </optgroup>
                ))}
              </select>
            </div>

            {error && <p className="text-[13px] text-[#DC2626]">{error}</p>}

            <button
              type="submit" disabled={submitting}
              className="w-full text-white font-bold py-3.5 rounded-[12px] transition disabled:opacity-60 text-[15px]"
              style={{ background: NAVY }}
            >
              {submitting ? 'Sending…' : 'Send my details'}
            </button>
            <p className="text-[11px] text-[#9CA3AF] text-center">
              By submitting this form you agree to be contacted by Padelante about your enquiry.
            </p>
          </form>
        )}
      </div>

      {showLevels && <LevelTableModal onClose={() => setShowLevels(false)} />}
    </div>
  );
}
